import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageIcon } from "@/components/PageIcon";
import { Plus, Search, X, Loader2, Pencil, Trash2 } from "lucide-react";

const fmt = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export default function MyNotes() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {} for new, {..} for edit

  const load = useCallback(() => {
    setLoading(true);
    api.get("/me/notes", { params: { q: q || undefined } })
      .then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/me/notes/${id}`); toast.success("Note deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-my-notes">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <PageIcon />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Notes</h1>
          <p className="text-sm text-slate-500">Private to you · {items.length} note{items.length === 1 ? "" : "s"}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes…" className="pl-9 h-9 w-56 bg-white" data-testid="notes-search" />
          </div>
          <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setEditing({})} data-testid="notes-add-btn"><Plus className="w-4 h-4" /> New note</Button>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-slate-400 text-sm mb-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center text-slate-400" data-testid="notes-empty">
          No notes yet. Click <span className="font-semibold text-slate-600">New note</span> to jot something down.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notes-grid">
        {items.map((n) => (
          <div key={n.id} className="group bg-white border border-slate-200 rounded-2xl shadow-sm p-4 flex flex-col" data-testid={`note-card-${n.id}`}>
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-slate-900 truncate">{n.title || "Untitled note"}</h3>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setEditing(n)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`note-edit-${n.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(n.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`note-delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-1.5 whitespace-pre-wrap line-clamp-6 flex-1">{n.body}</p>
            <div className="text-[11px] text-slate-400 mt-3">{fmt(n.updated_at)}</div>
          </div>
        ))}
      </div>

      {editing && <NoteEditor note={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function NoteEditor({ note, onClose, onSaved }) {
  const editing = !!note.id;
  const [title, setTitle] = useState(note.title || "");
  const [body, setBody] = useState(note.body || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!title.trim() && !body.trim()) { toast.error("Note is empty"); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/me/notes/${note.id}`, { title, body });
      else await api.post("/me/notes", { title, body });
      toast.success(editing ? "Note updated" : "Note saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose} data-testid="note-editor">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{editing ? "Edit note" : "New note"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="note-editor-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" autoFocus data-testid="note-title-input" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder="Write your note…"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="note-body-input" />
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="note-save-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
        </div>
      </div>
    </div>
  );
}
